import { useEffect, useMemo, useState } from "react";
import {
	Modal,
	Table,
	Button,
	Tag,
	Collapse,
	Empty,
	Tooltip,
	Spin,
	Input as AntdInput,
} from "antd";
import {
	UserOutlined,
	DeleteOutlined,
	UndoOutlined,
	SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import trans from "../../../utils";
import { Input, InputDate } from "../../Helpers/Input/Input";
import { InputSelect } from "../../Helpers/Input/RefactoredInput";
import { useTechnicians } from "../../../hooks/useTechnicians";
import { useGetServiceRequestsQuery } from "../../../Redux/service/Dispatcher/Dispatcher";
import {
	useGetSingleDispatchPlanQuery,
	useEditDispatchPlanMutation,
} from "../../../Redux/service/Dispatcher/DispatcherPlan";
import { useGetAllSkusQuery } from "../../../Redux/service/Sku/Sku";
import { EgyptGovernorates } from "../../../constants";
import { getMarkazOptions } from "../../../utils/CountryState/egyptMarkaz";
import { formatDateString } from "../../../JsHelpers/DateFormat";

// Same map Dispatcher.jsx uses for the Status column's Tag color.
const DISPATCH_STATUS_COLORS = {
	open: "default",
	assigned: "purple",
	pending: "orange",
	inProgress: "blue",
	completed: "green",
	closed: "cyan",
	cancelled: "red",
};

const SORT_FIELD_BY_COLUMN_KEY = {
	requestNumber: "requestNumber",
	preferredVisitTime: "preferredVisitTime",
	requestedDate: "requestedDate",
};

function idSetsEqual(a = [], b = []) {
	if (a.length !== b.length) return false;
	const setB = new Set(b);
	return a.every((v) => setB.has(v));
}

const DEFAULT_TOUCHED = {
	planName: false,
	assigneeId: false,
	planningDate: false,
};

export default function EditDispatchPlanModal({ planId, onClose }) {
	const { data, isFetching: planLoading } = useGetSingleDispatchPlanQuery(
		planId,
		{ skip: !planId, refetchOnMountOrArgChange: true },
	);
	const plan = data?.data;

	const [planName, setPlanName] = useState("");
	const [assigneeId, setAssigneeId] = useState(undefined);
	const [planningDate, setPlanningDate] = useState("");
	const [touched, setTouched] = useState(DEFAULT_TOUCHED);
	const [selectedRequests, setSelectedRequests] = useState([]);
	const [addedRequests, setAddedRequests] = useState([]);
	const [mutedIds, setMutedIds] = useState([]);
	const [originalIds, setOriginalIds] = useState([]);

	const [search, setSearch] = useState("");
	const [governorate, setGovernorate] = useState([]);
	const [markaz, setMarkaz] = useState([]);
	const [productId, setProductId] = useState([]);
	const [sort, setSort] = useState({
		sortBy: "requestedDate",
		sortOrder: "desc",
	});
	const [activeFilterKey, setActiveFilterKey] = useState(null);

	const { options: assigneeOptions, isFetching: assigneesLoading } =
		useTechnicians();
	const [editDispatchPlan, { isLoading: saving }] =
		useEditDispatchPlanMutation();

	useEffect(() => {
		if (!planId || !plan) return;
		setPlanName(plan.name || "");
		setAssigneeId(plan.assignedTo ? String(plan.assignedTo.id) : undefined);
		setPlanningDate(plan.plannedDate || "");
		setSelectedRequests(plan.serviceRequests || []);
		setAddedRequests([]);
		setMutedIds([]);
		setOriginalIds((plan.serviceRequests || []).map((r) => r.id));
		setTouched(DEFAULT_TOUCHED);
	}, [planId, plan]);

	useEffect(() => {
		if (!planId) {
			setSearch("");
			setGovernorate([]);
			setMarkaz([]);
			setProductId([]);
			setSort({ sortBy: "requestedDate", sortOrder: "desc" });
		}
	}, [planId]);

	const { data: skusData } = useGetAllSkusQuery();
	const productOptions = (skusData?.data || []).map((s) => ({
		label: s.skuName,
		value: String(s.id),
	}));
	const markazOptions = governorate.length
		? Array.from(
				new Map(
					governorate
						.flatMap((g) => getMarkazOptions(g))
						.map((o) => [o.value, o]),
				).values(),
			)
		: [];

	const mergedRequests = useMemo(() => {
		const map = new Map();
		selectedRequests.forEach((r) => map.set(r.id, r));
		addedRequests.forEach((r) => map.set(r.id, r));
		return Array.from(map.values());
	}, [selectedRequests, addedRequests]);

	const { data: availableData, isFetching: availableLoading } =
		useGetServiceRequestsQuery(
			{
				status: "open",
				page: 1,
				limit: 25,
				search: search || undefined,
				sortBy: sort.sortBy,
				sortOrder: sort.sortOrder,
				governorate: governorate.length ? governorate.join(",") : undefined,
				markaz: markaz.length ? markaz.join(",") : undefined,
				productId: productId.length ? productId.join(",") : undefined,
			},
			{ skip: !planId, refetchOnMountOrArgChange: true },
		);
	const availableRequests = availableData?.data || [];

	const isPlanNameInvalid = !planName.trim();
	const isAssigneeInvalid = !assigneeId;
	const isPlanningDateInvalid = !planningDate;

	const toggleMute = (id) => {
		setMutedIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
		);
	};

	const handleSave = async () => {
		setTouched({ planName: true, assigneeId: true, planningDate: true });
		if (isPlanNameInvalid || isAssigneeInvalid || isPlanningDateInvalid) {
			toast.error(trans("Please fill in all fields"), { autoClose: 2500 });
			return;
		}

		const finalIds = mergedRequests
			.filter((r) => !mutedIds.includes(r.id))
			.map((r) => r.id);

		const payload = {
			id: plan.id,
			name: planName.trim(),
			plannedDate: dayjs(planningDate).format("YYYY-MM-DD"),
			assignedToId: Number(assigneeId),
		};
		if (!idSetsEqual(finalIds, originalIds)) {
			payload.serviceRequestIds = finalIds;
		}

		try {
			await editDispatchPlan(payload).unwrap();
			toast.success(trans("Dispatch plan updated successfully"), {
				autoClose: 2000,
			});
			onClose();
		} catch (error) {
			toast.error(
				trans(error?.data?.message) || trans("Failed to update dispatch plan"),
				{ autoClose: 3000 },
			);
		}
	};

	const handleAvailableTableChange = (pagination, tableFilters, sorter) => {
		const nextSort = sorter.order
			? {
					sortBy: SORT_FIELD_BY_COLUMN_KEY[sorter.field] || "requestedDate",
					sortOrder: sorter.order === "ascend" ? "asc" : "desc",
				}
			: { sortBy: "requestedDate", sortOrder: "desc" };
		setSort(nextSort);

		const newGovernorate =
			activeFilterKey === "governorate"
				? tableFilters?.governorate || []
				: governorate;
		const governorateChanged = !idSetsEqual(newGovernorate, governorate);
		const newMarkaz = governorateChanged
			? []
			: activeFilterKey === "markaz"
				? tableFilters?.markaz || []
				: markaz;
		const newProductId =
			activeFilterKey === "product" ? tableFilters?.product || [] : productId;

		setGovernorate(newGovernorate);
		setMarkaz(newMarkaz);
		setProductId(newProductId);
	};

	const selectedColumns = [
		{
			title: trans("Request ID"),
			dataIndex: "requestNumber",
			key: "requestNumber",
			width: 150,
		},
		{
			title: trans("Status"),
			dataIndex: "status",
			key: "status",
			width: 130,
			render: (v) => (
				<Tag color={"orange"} className="text-capitalize">
					{trans(v)}
				</Tag>
			),
		},
		{
			title: trans("Government"),
			dataIndex: "governorate",
			key: "governorate",
			width: 160,
			render: (v) => (v ? trans(v) : "--"),
		},
		{
			title: trans("Markaz"),
			dataIndex: "markaz",
			key: "markaz",
			width: 130,
			render: (v) => (v ? trans(v) : "--"),
		},
		{
			title: trans("Village"),
			dataIndex: "village",
			key: "village",
			width: 150,
			render: (v) => v || "--",
		},
		{
			title: trans("Address"),
			dataIndex: "address",
			key: "address",
			width: 190,
			ellipsis: true,
			render: (v) => v || "--",
		},
		{
			title: trans("Product"),
			dataIndex: "product",
			key: "product",
			width: 150,
			render: (v) => v || "--",
		},
		{
			title: trans("Model"),
			dataIndex: "model",
			key: "model",
			width: 140,
			render: (v) => v || "--",
		},
		{
			title: trans("Complaint Summary"),
			dataIndex: "complaintSummary",
			key: "complaintSummary",
			width: 220,
			ellipsis: true,
			render: (v) => v || "--",
		},
		{
			title: trans("Preferred Visit Time"),
			key: "preferredVisitTime",
			width: 260,
			render: (_, record) => {
				if (!record.preferredVisitTime) return "--";
				return record.preferredVisitTimeEnd
					? `${formatDateString(record.preferredVisitTime)} - ${formatDateString(record.preferredVisitTimeEnd)}`
					: formatDateString(record.preferredVisitTime);
			},
		},
		{
			title: trans("Requested Date"),
			dataIndex: "requestedDate",
			key: "requestedDate",
			width: 190,
			render: (v) => (v ? formatDateString(v) : "--"),
		},
		{
			title: trans("Current Assignee"),
			key: "currentAssignee",
			width: 150,
			render: (_, record) =>
				record.currentAssignee?.name || trans("Unassigned"),
		},
		{
			title: "",
			key: "actions",
			width: 60,
			render: (_, r) => {
				const muted = mutedIds.includes(r.id);
				return (
					<Tooltip title={muted ? trans("Restore") : trans("Remove")}>
						<Button
							type="text"
							danger={!muted}
							icon={muted ? <UndoOutlined /> : <DeleteOutlined />}
							onClick={() => toggleMute(r.id)}
						/>
					</Tooltip>
				);
			},
		},
	];

	const availableColumns = [
		{
			title: trans("Request ID"),
			dataIndex: "requestNumber",
			key: "requestNumber",
			width: 200,
			sorter: true,
		},
		{
			title: trans("Status"),
			dataIndex: "status",
			key: "status",
			width: 130,
			render: (v) => (
				<Tag
					color={DISPATCH_STATUS_COLORS[v] || "default"}
					className="text-capitalize"
				>
					{trans(v)}
				</Tag>
			),
		},
		{
			title: trans("Government"),
			dataIndex: "governorate",
			key: "governorate",
			width: 160,
			filters: EgyptGovernorates.map((g) => ({
				text: g.label,
				value: g.value,
			})),
			filterSearch: true,
			filterMultiple: true,
			filteredValue: governorate.length ? governorate : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (isOpen) => {
				if (isOpen) setActiveFilterKey("governorate");
			},
			render: (v) => (v ? trans(v) : "--"),
		},
		{
			title: trans("Markaz"),
			dataIndex: "markaz",
			key: "markaz",
			width: 130,
			filters: markazOptions.length
				? markazOptions.map((o) => ({ text: o.label, value: o.value }))
				: undefined,
			filterSearch: true,
			filterMultiple: true,
			filteredValue: markaz.length ? markaz : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (isOpen) => {
				if (isOpen) setActiveFilterKey("markaz");
			},
			render: (v) => (v ? trans(v) : "--"),
		},
		{
			title: trans("Village"),
			dataIndex: "village",
			key: "village",
			width: 150,
			render: (v) => v || "--",
		},
		{
			title: trans("Address"),
			dataIndex: "address",
			key: "address",
			width: 190,
			ellipsis: true,
			render: (v) => v || "--",
		},
		{
			title: trans("Product"),
			dataIndex: "product",
			key: "product",
			width: 150,
			filters: productOptions.map((o) => ({ text: o.label, value: o.value })),
			filterSearch: true,
			filterMultiple: true,
			filteredValue: productId.length ? productId : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (isOpen) => {
				if (isOpen) setActiveFilterKey("product");
			},
			render: (v) => v || "--",
		},
		{
			title: trans("Model"),
			dataIndex: "model",
			key: "model",
			width: 140,
			render: (v) => v || "--",
		},
		{
			title: trans("Complaint Summary"),
			dataIndex: "complaintSummary",
			key: "complaintSummary",
			width: 220,
			ellipsis: true,
			render: (v) => v || "--",
		},
		{
			title: trans("Preferred Visit Time"),
			key: "preferredVisitTime",
			width: 260,
			sorter: true,
			render: (_, record) => {
				if (!record.preferredVisitTime) return "--";
				return record.preferredVisitTimeEnd
					? `${formatDateString(record.preferredVisitTime)} - ${formatDateString(record.preferredVisitTimeEnd)}`
					: formatDateString(record.preferredVisitTime);
			},
		},
		{
			title: trans("Requested Date"),
			dataIndex: "requestedDate",
			key: "requestedDate",
			width: 190,
			sorter: true,
			render: (v) => (v ? formatDateString(v) : "--"),
		},
		{
			title: trans("Current Assignee"),
			key: "currentAssignee",
			width: 150,
			render: (_, record) =>
				record.currentAssignee?.name || trans("Unassigned"),
		},
	];

	return (
		<Modal
			title={trans("Edit Dispatch Plan")}
			open={!!planId}
			onCancel={onClose}
			onClose={onClose}
			width={860}
			centered
			maskClosable={false}
			styles={{
				body: { maxHeight: "75vh", overflowY: "auto", paddingRight: 4 },
			}}
			footer={[
				<Button key="cancel" onClick={onClose} disabled={saving}>
					{trans("Cancel")}
				</Button>,
				<Button
					key="save"
					type="primary"
					loading={saving}
					disabled={planLoading || !plan}
					onClick={handleSave}
				>
					{trans("Save")}
				</Button>,
			]}
		>
			{planLoading || !plan ? (
				<div
					className="d-flex justify-content-center align-items-center"
					style={{ minHeight: 200 }}
				>
					<Spin />
				</div>
			) : (
				<>
					<p className="fs-16 fw-500 brand-700 text-capitalize mb-3">
						{trans("General Information")}
					</p>
					<div className="row">
						<div className="col-lg-6">
							<Input
								title={trans("Plan Name")}
								type="text"
								id="editPlanName"
								name="editPlanName"
								value={planName}
								onChange={(e) => setPlanName(e.target.value)}
								onBlur={() =>
									setTouched((prev) => ({ ...prev, planName: true }))
								}
								errors={isPlanNameInvalid ? "Plan Name is required" : ""}
								touched={touched.planName}
							/>
						</div>
						<div className="col-lg-6">
							<InputSelect
								id="editAssigneeId"
								name="editAssigneeId"
								label={trans("Assignee To")}
								placeholder={trans("Select field staff")}
								options={assigneeOptions}
								loading={assigneesLoading}
								value={assigneeId}
								onChange={(val) => setAssigneeId(val)}
								onBlur={() =>
									setTouched((prev) => ({ ...prev, assigneeId: true }))
								}
								errors={isAssigneeInvalid ? "Assignee is required" : ""}
								touched={touched.assigneeId}
								formstyle="select-z-index"
								allowClear={false}
								optionRender={(option) => (
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "8px",
										}}
									>
										{option.data.avatar ? (
											<img
												src={option.data.avatar}
												alt={option.label}
												style={{ width: 24, height: 24, borderRadius: "50%" }}
											/>
										) : (
											<UserOutlined
												style={{ width: 24, height: 24, borderRadius: "50%" }}
											/>
										)}
										<span>{option.label}</span>
									</div>
								)}
							/>
						</div>
						<div className="col-lg-6">
							<InputDate
								title={trans("Planning Date")}
								id="editPlanningDate"
								name="editPlanningDate"
								formstyle="h-25"
								onChange={(value) =>
									setPlanningDate(value ? dayjs(value).toISOString() : "")
								}
								onBlur={() =>
									setTouched((prev) => ({ ...prev, planningDate: true }))
								}
								value={planningDate || ""}
								errors={
									isPlanningDateInvalid ? "Planning Date is required" : ""
								}
								touched={touched.planningDate}
							/>
						</div>
					</div>

					<Collapse
						className="mt-3"
						defaultActiveKey={["selected"]}
						items={[
							{
								key: "selected",
								label: (
									<div>
										<div className="fw-600">{trans("Selected Request")}</div>
										<div className="neutral-400" style={{ fontSize: 12 }}>
											{trans(
												"Requests currently included in this dispatch plan",
											)}
										</div>
									</div>
								),
								style: { background: "#eef7f1" },
								children: selectedRequests.length ? (
									<Table
										rowKey="id"
										columns={selectedColumns}
										dataSource={selectedRequests}
										pagination={false}
										scroll={{ x: "max-content" }}
										onRow={(record) => ({
											style: mutedIds.includes(record.id)
												? { opacity: 0.5, textDecoration: "line-through" }
												: undefined,
										})}
									/>
								) : (
									<Empty description={trans("This plan has no requests")} />
								),
							},
							{
								key: "available",
								label: (
									<div>
										<div className="fw-600">{trans("Available Requests")}</div>
										<div className="neutral-400" style={{ fontSize: 12 }}>
											{trans("Available requests for this plan")}
										</div>
									</div>
								),
								style: { background: "#f5f6f7" },
								children: (
									<>
										<div className="d-flex justify-content-start mb-2">
											<AntdInput
												prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
												placeholder={trans("Search")}
												value={search}
												onChange={(e) => setSearch(e.target.value)}
												style={{ width: 220 }}
												allowClear
											/>
										</div>
										<Table
											rowKey="id"
											loading={availableLoading}
											columns={availableColumns}
											dataSource={availableRequests}
											pagination={false}
											scroll={{ x: "max-content", y: 120 }}
											onChange={handleAvailableTableChange}
											rowSelection={{
												selectedRowKeys: addedRequests.map((r) => r.id),
												onChange: (_keys, rows) => setAddedRequests(rows),
											}}
										/>
									</>
								),
							},
						]}
					/>
				</>
			)}
		</Modal>
	);
}
